import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { API_ENDPOINTS } from '../config/api';

const Services = () => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    fetchServices();
  }, []);

  const fetchServices = async () => {
    try {
      setLoading(true);
      setError('');
      
      console.log('Fetching services from:', API_ENDPOINTS.PUBLIC.SERVICES);
      
      const response = await axios.get(API_ENDPOINTS.PUBLIC.SERVICES, {
        timeout: 8000, // 8 second timeout
      });
      
      console.log('Services data:', response.data);
      
      if (response.data && Array.isArray(response.data)) {
        setServices(response.data);
      } else {
        throw new Error('Invalid data format received');
      }
      
    } catch (error) {
      console.error('Error fetching services:', error);
      
      // Enhanced error handling
      if (error.code === 'ECONNABORTED') {
        setError('Request timeout. Please check your connection and try again.');
      } else if (error.response) {
        // Server responded with error status
        setError(`Server error: ${error.response.status}. Please try again later.`);
      } else if (error.request) {
        // Network error
        setError('Network error. Please check your connection and ensure the server is running.');
      } else {
        setError('Unable to load services. Please try again later.');
      }
      
      setServices([]);
    } finally {
      setLoading(false);
    }
  };

  const handleRetry = () => {
    fetchServices();
  };

  const handleContactClick = () => {
    navigate('/about');
    // Optional: Scroll to contact section after navigation
    setTimeout(() => {
      const contactSection = document.getElementById('contact-section');
      if (contactSection) {
        contactSection.scrollIntoView({ behavior: 'smooth' });
      }
    }, 100);
  };

  if (loading) {
    return (
      <div className="container py-5">
        <div className="text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-3">Loading services...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container py-5">
        <div className="alert alert-warning text-center">
          <i className="fas fa-exclamation-triangle me-2"></i>
          {error}
          <div className="mt-3">
            <button className="btn btn-primary btn-sm" onClick={handleRetry}>
              <i className="fas fa-redo me-2"></i>
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container py-5">
      {/* Header with disclaimer */}
      <div className="text-center mb-5">
        <h1 className="display-4 fw-bold text-primary mb-3">Our Tailoring Services</h1>
        <p className="lead text-muted mb-4">
          Professional tailoring services with perfect fit guaranteed
        </p>
        <div className="alert alert-info mx-auto" style={{ maxWidth: '600px' }}>
          <i className="fas fa-info-circle me-2"></i>
          <strong>Pricing Note:</strong> All prices are indicative and may vary based on 
          fabric selection, design complexity, and specific requirements.
        </div>
      </div>
      
      {services.length === 0 ? (
        <div className="text-center py-5">
          <i className="fas fa-scissors fa-3x text-muted mb-3"></i>
          <p className="text-muted">No services available at the moment.</p>
          <p className="text-muted small">Please check back later or contact us for custom tailoring requests.</p>
        </div>
      ) : (
        <>
          {/* Services Grid */}
          <div className="row">
            {services.map(service => (
              <div key={service.id} className="col-md-4 mb-4">
                <div className="card service-card h-100 shadow-sm border-0">
                  <div className="card-body text-center d-flex flex-column p-4">
                    <div className="mb-3">
                      <i className="fas fa-tshirt text-primary fs-1"></i>
                    </div>
                    <h5 className="card-title text-dark fw-bold mb-3">{service.name}</h5>
                    <p className="card-text text-muted flex-grow-1">{service.description}</p>
                    <div className="mt-auto">
                      <div className="mb-2">
                        <strong className="text-success" style={{fontSize: '1.3rem'}}>
                          Starting from ₹{service.price}
                        </strong>
                      </div>
                      <small className="text-muted">
                        <i className="fas fa-info-circle me-1"></i>
                        Final price depends on requirements
                      </small>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Enhanced Final CTA Section */}
          <div className="text-center mt-5">
            <div className="bg-gradient-primary text-white p-5 rounded-3 shadow-lg position-relative overflow-hidden">
              {/* Background Decoration */}
              <div className="position-absolute top-0 end-0 mt-3 me-3 opacity-25">
                <i className="fas fa-scissors fa-4x"></i>
              </div>
              <div className="position-absolute bottom-0 start-0 mb-3 ms-3 opacity-25">
                <i className="fas fa-ruler-combined fa-4x"></i>
              </div>
              
              <div className="position-relative">
                <h3 className="h2 fw-bold mb-3">Ready to Get Your Perfect Fit?</h3>
                <p className="fs-5 mb-4 opacity-90">
                  Contact us today for exact pricing and personalized consultation. 
                  Let's create something amazing together!
                </p>
                
                <div className="row justify-content-center g-3">
                  <div className="col-auto">
                    <button 
                      className="btn btn-light btn-lg px-4 py-3 fw-bold shadow-sm"
                      onClick={handleContactClick}
                      style={{
                        transition: 'all 0.3s ease',
                        border: '2px solid rgba(255,255,255,0.3)'
                      }}
                      onMouseOver={(e) => {
                        e.target.style.transform = 'translateY(-2px)';
                        e.target.style.boxShadow = '0 8px 25px rgba(0,0,0,0.15)';
                      }}
                      onMouseOut={(e) => {
                        e.target.style.transform = 'translateY(0)';
                        e.target.style.boxShadow = '0 4px 15px rgba(0,0,0,0.1)';
                      }}
                    >
                      <i className="fas fa-calendar-check me-2 text-primary"></i>
                      Get Exact Quote & Consultation
                    </button>
                  </div>
                  <div className="col-auto">
                    <button 
                      className="btn btn-outline-light btn-lg px-4 py-3 fw-bold"
                      onClick={() => navigate('/about')}
                    >
                      <i className="fas fa-store me-2"></i>
                      Visit Our Shop
                    </button>
                  </div>
                </div>
                
                <div className="mt-4">
                  <div className="row justify-content-center g-4">
                    <div className="col-auto">
                      <div className="d-flex align-items-center">
                        <i className="fas fa-clock text-warning me-2"></i>
                        <small>Mon-Sat: 9AM-8PM</small>
                      </div>
                    </div>
                    <div className="col-auto">
                      <div className="d-flex align-items-center">
                        <i className="fas fa-phone text-success me-2"></i>
                        <small>+91 9840322598</small>
                      </div>
                    </div>
                    <div className="col-auto">
                      <div className="d-flex align-items-center">
                        <i className="fas fa-map-marker-alt text-danger me-2"></i>
                        <small>Fathima Fashion, Tirunelveli</small>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      <style jsx>{`
        .bg-gradient-primary {
          background: linear-gradient(135deg, #0d6efd 0%, #0a58ca 100%);
        }
        .service-card {
          transition: all 0.3s ease;
        }
        .service-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 12px 30px rgba(0,0,0,0.15) !important;
        }
      `}</style>
    </div>
  );
};

export default Services;